import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { FiSearch, FiInfo, FiLoader, FiTrash2 } from 'react-icons/fi';
import '../styles/carTows.scss';
import { useCarTows } from '../hooks/useCarTows';
import { alertActions } from '../redux/AlertController';

export const CarTows = () => {
    const user = useSelector(state => state.userController.user);
    const navigate = useNavigate();
    const [rejectModal, setRejectModal] = useState({ show: false, id: null });
    const [rejectReason, setRejectReason] = useState('');
    const [showAllRequests, setShowAllRequests] = useState(false);
    const [showUpdateRequests, setShowUpdateRequests] = useState(false);
    const [showDriverInfo, setShowDriverInfo] = useState(null);
    const [driverNumberDraft, setDriverNumberDraft] = useState('');
    const [searchInput, setSearchInput] = useState('');
    const [filterCity, setFilterCity] = useState('');
    const [filterArea, setFilterArea] = useState('');
    const [filterState, setFilterState] = useState('');
    const [loadingAction, setLoadingAction] = useState({ type: null, id: null });
    const [locationEdits, setLocationEdits] = useState({});
    const [updateLocationEdits, setUpdateLocationEdits] = useState({});
    const [focusedField, setFocusedField] = useState(null); // { id, type: 'city' | 'area', isUpdate: boolean }
    const {
        drivers,
        allRequests,
        requestsCount,
        updateRequests,
        updateRequestsCount,
        locations,
        isLoading,
        currentPage,
        totalPages,
        totals,
        driverFilters,
        handleSearch,
        handleApprove,
        handleReject,
        handleApproveUpdate,
        handleRejectUpdate,
        updateVerificationLocation,
        updateDriverUpdateLocation,
        addCityWithArea,
        addAreaToCity,
        fetchDrivers,
        applyDriverFilters,
        clearDriverFilters,
        fetchAllRequests,
        fetchUpdateRequests,
        toggleDriverBan,
        updateWarnings,
        reactivateDriver,
        deleteDriver,
        updateDriverNumber,
        updatePaymentMethods
    } = useCarTows();

    console.log('request', allRequests);

    const dispatch = useDispatch();

    useEffect(() => {
        if (!user) {
            navigate('/login');
        }
    }, [user]);

    useEffect(() => {
        setDriverNumberDraft(showDriverInfo?.driverNumber ? String(showDriverInfo.driverNumber) : '');
    }, [showDriverInfo]);

    const handleRejectClick = (id) => {
        setRejectModal({ show: true, id });
    };

    const confirmReject = async () => {
        if (!rejectReason.trim()) return;
        setLoadingAction({ type: 'reject', id: rejectModal.id });
        const result = await handleReject(rejectModal.id, rejectReason);
        setLoadingAction({ type: null, id: null });
        if (result) {
            setRejectModal({ show: false, id: null });
            setRejectReason('');
        }
    };

    const ensureLocationBeforeApprove = async (requestId, isUpdate = false) => {
        const list = isUpdate ? updateRequests : allRequests;
        const request = list.find((r) => String(r._id) === String(requestId));
        if (!request) return false;

        const edits = (isUpdate ? updateLocationEdits[requestId] : locationEdits[requestId]) || {};
        const city = (edits.city ?? request.city ?? '').trim();
        const area = (edits.area ?? request.area ?? '').trim();

        // If nothing to update, skip location changes
        if (!city && !area) return true;

        const cityMatch = (locations || []).find(
            (loc) => (loc.city || '').toLowerCase() === city.toLowerCase()
        );
        const areaMatch = cityMatch?.areas?.some((a) => {
            const name = typeof a === 'string' ? a : (a && typeof a.name === 'string' ? a.name : '');
            return name.toLowerCase() === area.toLowerCase();
        });

        if (city && !cityMatch) {
            const added = await addCityWithArea(city, area);
            if (!added) return false;
        } else if (city && area && !areaMatch) {
            const added = await addAreaToCity(city, area);
            if (!added) return false;
        }

        const updated = isUpdate
            ? await updateDriverUpdateLocation(requestId, city, area)
            : await updateVerificationLocation(requestId, city, area);
        return Boolean(updated);
    };

    const handleApproveClick = async (requestId) => {
        setLoadingAction({ type: 'approve', id: requestId });
        const ok = await ensureLocationBeforeApprove(requestId, false);
        if (ok) {
            await handleApprove(requestId);
        }
        setLoadingAction({ type: null, id: null });
    };

    const handleApproveUpdateClick = async (requestId) => {
        setLoadingAction({ type: 'approve-update', id: requestId });
        const ok = await ensureLocationBeforeApprove(requestId, true);
        if (ok) {
            await handleApproveUpdate(requestId);
        }
        setLoadingAction({ type: null, id: null });
    };

    const handleToggleBan = async (driverId) => {
        setLoadingAction({ type: 'ban', id: driverId });
        const result = await toggleDriverBan(driverId);
        if (result && showDriverInfo?._id === driverId) {
            setShowDriverInfo(prev => ({ ...prev, isBanned: result.isBanned }));
        }
        setLoadingAction({ type: null, id: null });
    };

    const handleUpdateWarnings = async (driverId, warnings) => {
        setLoadingAction({ type: 'warnings', id: driverId });
        const result = await updateWarnings(driverId, warnings);
        if (result && showDriverInfo?._id === driverId) {
            setShowDriverInfo(prev => ({ ...prev, warrnings: warnings }));
        }
        setLoadingAction({ type: null, id: null });
    };

    const confirmRejectUpdate = async () => {
        if (!rejectReason.trim()) return;
        setLoadingAction({ type: 'reject-update', id: rejectModal.id });
        const result = await handleRejectUpdate(rejectModal.id, rejectReason);
        setLoadingAction({ type: null, id: null });
        if (result) {
            setRejectModal({ show: false, id: null });
            setRejectReason('');
        }
    };

    const handleReactivateDriver = async (driverId) => {
        setLoadingAction({ type: 'reactivate', id: driverId });
        const result = await reactivateDriver(driverId);
        if (result && showDriverInfo?._id === driverId) {
            setShowDriverInfo(prev => ({ ...prev, ...result }));
        }
        setLoadingAction({ type: null, id: null });
    };

    const handleDeleteDriver = async (driverId) => {
        const confirmed = window.confirm('هل أنت متأكد من حذف هذا السائق بشكل نهائي؟');
        if (!confirmed) return;
        setLoadingAction({ type: 'delete', id: driverId });
        const result = await deleteDriver(driverId);
        if (result && showDriverInfo?._id === driverId) {
            setShowDriverInfo(null);
        }
        setLoadingAction({ type: null, id: null });
    };

    const handleSaveDriverNumber = async (driverId) => {
        const parsed = Number.parseInt(driverNumberDraft, 10);
        if (!Number.isInteger(parsed) || parsed <= 0) {
            dispatch(alertActions.showAlert({ msg: 'رقم السائق يجب أن يكون رقماً صحيحاً أكبر من 0', type: 'error' }));
            return;
        }

        setLoadingAction({ type: 'driver-number', id: driverId });
        const result = await updateDriverNumber(driverId, parsed);
        if (result && showDriverInfo?._id === driverId) {
            setShowDriverInfo(prev => ({ ...prev, driverNumber: result.driverNumber }));
            setDriverNumberDraft(String(result.driverNumber));
        }
        setLoadingAction({ type: null, id: null });
    };

    const handleUpdatePaymentMethod = async (driverId, field, value) => {
        setLoadingAction({ type: `payment-${field}`, id: driverId });
        const payload = { [field]: value };
        const result = await updatePaymentMethods(driverId, payload);
        if (result && showDriverInfo?._id === driverId) {
            setShowDriverInfo(prev => ({ ...prev, ...result }));
        }
        setLoadingAction({ type: null, id: null });
    };

    const driverRatings = Array.isArray(showDriverInfo?.ratings) ? showDriverInfo.ratings : [];

    const averageRating = (() => {
        if (!driverRatings.length) return 0;
        const sum = driverRatings.reduce((acc, r) => acc + (r.rating || 0), 0);
        return +(sum / driverRatings.length).toFixed(2);
    })();

    const getStatusClass = (status) => {
        switch (status) {
            case 'active': return 'approved';
            case 'inactive': return 'pending';
            case 'deleted': return 'deleted';
            default: return '';
        }
    };

    const getStatusText = (status) => {
        switch (status) {
            case 'active': return 'نشط';
            case 'inactive': return 'غير نشط';
            case 'deleted': return 'محذوف';
            default: return status;
        }
    };

    const handleSearchSubmit = () => {
        handleSearch(searchInput);
    };

    const pendingRequests = allRequests.filter(request => request.state === 'pending');
    console.log(pendingRequests)
    const pendingUpdateRequests = updateRequests.filter(request => request.state === 'pending');

    const cityOptions = Array.from(new Set((locations || []).map((loc) => loc.city).filter(Boolean)))
        .sort((a, b) => String(a).localeCompare(String(b), 'ar', { sensitivity: 'base' }));
    const areaOptions = (() => {
        const match = (locations || []).find((loc) => (loc.city || '').toLowerCase() === (filterCity || '').toLowerCase());
        return (match?.areas || [])
            .map((a) => a.name)
            .filter(Boolean)
            .sort((a, b) => String(a).localeCompare(String(b), 'ar', { sensitivity: 'base' }));
    })();

    const isActionLoading = (type, id) => loadingAction.type === type && loadingAction.id === id;

    const getDriverInactiveReason = (driver) => {
        if (!driver || driver.state === 'active') return '';
        if (driver.state === 'deleted') return 'الحساب محذوف';
        if (driver.isBanned) return 'الحساب محظور';
        if (driver.verificationStatus === 'didnt-apply') return 'لم يقم بتقديم طلب التفعيل بعد';
        if (driver.verificationStatus === 'rejected') {
            const reason = (driver.verificationReason || '').trim();
            return reason ? `تم رفض طلب التفعيل: ${reason}` : 'تم رفض طلب التفعيل';
        }
        if (driver.verificationStatus === 'approved') {
            return 'تمت الموافقة لكن الاشتراك منتهي/غير مشترك';
        }
        return 'غير نشط';
    };

    const getLocationEdit = (id, isUpdate = false) => {
        const source = isUpdate ? updateLocationEdits : locationEdits;
        return source[id] || {};
    };

    const getCitySuggestions = (term = '') => {
        const q = term?.trim().toLowerCase() || '';
        return (locations || [])
            .map((loc) => (typeof loc.city === 'string' ? loc.city : ''))
            .filter(Boolean)
            .filter((name, idx, arr) => arr.indexOf(name) === idx)
            .filter((name) => !q || name.toLowerCase().includes(q))
            .slice(0, 6);
    };

    const getAreaSuggestions = (city = '', term = '') => {
        const cityName = typeof city === 'string' ? city.trim() : '';
        if (!cityName) return [];
        const match = (locations || []).find((loc) => (loc.city || '').toLowerCase() === cityName.toLowerCase());
        if (!match) return [];
        const list = (match.areas || []).map((a) => {
            if (typeof a === 'string') return a;
            if (a && typeof a.name === 'string') return a.name;
            return '';
        });
        const q = term?.trim().toLowerCase() || '';
        return list
            .filter(Boolean)
            .filter((name, idx, arr) => arr.indexOf(name) === idx)
            .filter((name) => !q || name.toLowerCase().includes(q))
            .slice(0, 6);
    };

    const setLocationEdit = (id, data, isUpdate = false) => {
        if (isUpdate) {
            setUpdateLocationEdits(prev => ({ ...prev, [id]: { ...(prev[id] || {}), ...data } }));
        } else {
            setLocationEdits(prev => ({ ...prev, [id]: { ...(prev[id] || {}), ...data } }));
        }
    };

    const applyLocation = async (requestId, isUpdate = false) => {
        const edit = getLocationEdit(requestId, isUpdate);
        const request = (isUpdate ? updateRequests : allRequests).find(r => r._id === requestId) || {};
        const city = (edit.city ?? request.city ?? '').trim();
        const area = (edit.area ?? request.area ?? '').trim();
        if (!city || !area) {
            dispatch(alertActions.showAlert({ msg: 'المدينة والمنطقة مطلوبة', type: 'error' }));
            return;
        }
        setLoadingAction({ type: isUpdate ? 'save-update-location' : 'save-location', id: requestId });
        const fn = isUpdate ? updateDriverUpdateLocation : updateVerificationLocation;
        await fn(requestId, city, area);
        setLoadingAction({ type: null, id: null });
    };

    useEffect(() => {
        setFilterCity(driverFilters.city || '');
        setFilterArea(driverFilters.area || '');
        setFilterState(driverFilters.state || '');
    }, [driverFilters.city, driverFilters.area, driverFilters.state]);

    if (isLoading) {
        return (
            <div className="loading-container">
                <div className="loading-spinner"></div>
            </div>
        );
    }

    return (
        <section className='car-tows-page'>
            <div className="header">
                <button 
                    className="review-requests-btn"
                    onClick={() => {
                        setShowAllRequests(true);
                        fetchAllRequests();
                    }}
                >
                    طلبات المراجعة
                    {requestsCount > 0 && <span className="count">{requestsCount}</span>}
                </button>
                <button
                    className="review-requests-btn secondary"
                    onClick={() => {
                        setShowUpdateRequests(true);
                        fetchUpdateRequests();
                    }}
                >
                    طلبات التحديث
                    {updateRequestsCount > 0 && <span className="count">{updateRequestsCount}</span>}
                </button>
                <div className="search-bar">
                    <input
                        type="text"
                        placeholder="بحث بالاسم أو رقم الهاتف أو رقم السائق..."
                        value={searchInput}
                        onChange={(e) => setSearchInput(e.target.value)}
                    />
                    <button className="search-btn" onClick={handleSearchSubmit}>
                        <FiSearch className="search-icon" />
                        بحث
                    </button>
                </div>
            </div>

            <div className="filters-bar">
                <div className="filter-group">
                    <label>المدينة</label>
                    <select
                        value={filterCity}
                        onChange={(e) => {
                            const value = e.target.value;
                            setFilterCity(value);
                            setFilterArea('');
                        }}
                    >
                        <option value="">الكل</option>
                        {cityOptions.map((city) => (
                            <option key={city} value={city}>{city}</option>
                        ))}
                    </select>
                </div>
                <div className="filter-group">
                    <label>المنطقة</label>
                    <select
                        value={filterArea}
                        onChange={(e) => setFilterArea(e.target.value)}
                        disabled={!filterCity}
                    >
                        <option value="">{filterCity ? 'الكل' : 'اختر المدينة أولا'}</option>
                        {areaOptions.map((area) => (
                            <option key={area} value={area}>{area}</option>
                        ))}
                    </select>
                </div>
                <div className="filter-group">
                    <label>حالة السائق</label>
                    <select
                        value={filterState}
                        onChange={(e) => setFilterState(e.target.value)}
                    >
                        <option value="">الكل</option>
                        <option value="active">نشط</option>
                        <option value="inactive">غير نشط</option>
                    </select>
                </div>
                <div className="filter-actions">
                    <button
                        className="apply-filter"
                        onClick={() => applyDriverFilters({ city: filterCity.trim(), area: filterArea.trim(), state: filterState })}
                    >
                        تطبيق التصفية
                    </button>
                    <button
                        className="clear-filter"
                        onClick={() => {
                            setFilterCity('');
                            setFilterArea('');
                            setFilterState('');
                            clearDriverFilters();
                        }}
                    >
                        مسح التصفية
                    </button>
                </div>
            </div>

            <div className="stats-cards">
                <div className="stat-card">
                    <p>إجمالي السائقين</p>
                    <h3>{totals.total}</h3>
                </div>
                <div className="stat-card">
                    <p>السائقون النشطون</p>
                    <h3>{totals.active}</h3>
                </div>
                <div className="stat-card">
                    <p>السائقون غير النشطين</p>
                    <h3>{totals.inactive}</h3>
                </div>
            </div>

            <div className="drivers-table">
                <table>
                    <thead>
                        <tr>
                            <th>رقم السائق</th>
                            <th>اسم السائق</th>
                            <th>رقم الهاتف</th>
                            <th>نوع المركبة</th>
                            <th>موديل المركبة</th>
                            <th>رقم اللوحة</th>
                            <th>عدد الرحلات</th>
                            <th>المدينة</th>
                            <th>المنطقة</th>
                            <th>الحالة</th>
                            <th>الإجراءات</th>
                        </tr>
                    </thead>
                    <tbody>
                        {drivers?.map((driver) => (
                            <tr key={driver._id}>
                                <td>{driver.driverNumber || '—'}</td>
                                <td>{driver.driverName}</td>
                                <td>{driver.phoneNumber}</td>
                                <td>{driver.vechicleType || 'لا يوجد'}</td>
                                <td>{driver.vechicleModel || 'لا يوجد'}</td>
                                <td>{driver.carPlate || 'لا يوجد'}</td>
                                <td>{typeof driver.numberOfTrips === 'number' ? driver.numberOfTrips : 0}</td>
                                <td>{driver.city || '—'}</td>
                                <td>{driver.area || '—'}</td>
                                <td>
                                    <span className={`status ${getStatusClass(driver.state)}`}>
                                        {getStatusText(driver.state)}
                                    </span>
                                </td>
                                <td>
                                    <button 
                                        className="info-btn"
                                        onClick={() => setShowDriverInfo(driver)}
                                    >
                                        <FiInfo /> معلومات السائق
                                    </button>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>

            {totalPages > 1 && (
                <div className="pagination">
                    <button
                        onClick={() => fetchDrivers(currentPage - 1)}
                        disabled={currentPage === 1}
                    >
                        السابق
                    </button>
                    {[...Array(totalPages)].map((_, i) => (
                        <button
                            key={i + 1}
                            onClick={() => fetchDrivers(i + 1)}
                            className={currentPage === i + 1 ? 'active' : ''}
                        >
                            {i + 1}
                        </button>
                    ))}
                    <button
                        onClick={() => fetchDrivers(currentPage + 1)}
                        disabled={currentPage === totalPages}
                    >
                        التالي
                    </button>
                </div>
            )}

            {/* All Requests Modal */}
            {showAllRequests && (
                <div className="modal">
                    <div className="modal-content requests-modal">
                        <h2>طلبات المراجعة</h2>
                        <div className="requests-list">
                            {pendingRequests.length === 0 ? (
                                <p className="no-requests">لا توجد طلبات</p>
                            ) : (
                                pendingRequests.map((request) => (
                                    <div key={request._id} className="request-card">
                                        <div className="media-row">
                                            <div className="media-item">
                                                <p>صورة المركبة</p>
                                                {request.carImage ? (
                                                    <img 
                                                        src={request.carImage} 
                                                        alt="car" 
                                                        className="car-image"
                                                    />
                                                ) : <span className="media-missing">لا يوجد</span>}
                                            </div>
                                            <div className="media-item">
                                                <p>رخصة القيادة (أمام)</p>
                                                {request.driverLicenseFront ? (
                                                    <img
                                                        src={request.driverLicenseFront}
                                                        alt="driver license front"
                                                        className="car-image"
                                                    />
                                                ) : <span className="media-missing">لا يوجد</span>}
                                            </div>
                                            <div className="media-item">
                                                <p>رخصة القيادة (خلف)</p>
                                                {request.driverLicenseBack ? (
                                                    <img
                                                        src={request.driverLicenseBack}
                                                        alt="driver license back"
                                                        className="car-image"
                                                    />
                                                ) : <span className="media-missing">لا يوجد</span>}
                                            </div>
                                        </div>
                                        <div className="request-info">
                                            <p>اسم السائق: {request.driverName}</p>
                                            <p>رقم الهاتف: {request.phoneNumber}</p>
                                            <p>نوع المركبة: {request.vechicleType}</p>
                                            <p>موديل المركبة: {request.vechicleModel}</p>
                                            <p>اللوحة: {request.carPlate}</p>
                                            <p>وقت الطلب: {new Date(request.updatedAt).toLocaleString('ar')}</p>    
                                            <p>
                                                المدينة: <span className={`flag ${request.cityExists ? 'flag-ok' : 'flag-missing'}`}>
                                                    {request.city || '—'}
                                                </span>
                                            </p>
                                            <p>
                                                المنطقة: <span className={`flag ${request.areaExists ? 'flag-ok' : 'flag-missing'}`}>
                                                    {request.area || '—'}
                                                </span>
                                            </p>
                                            
                                            <div className="location-edit">
                                                {(() => {
                                                    const currentCity = (getLocationEdit(request._id).city ?? request.city ?? '').toString();
                                                    const currentArea = (getLocationEdit(request._id).area ?? request.area ?? '').toString();
                                                    const citySuggestions = getCitySuggestions(currentCity);
                                                    const areaSuggestions = getAreaSuggestions(currentCity, currentArea);
                                                    const showCitySuggestions = focusedField && !focusedField.isUpdate && focusedField.id === request._id && focusedField.type === 'city';
                                                    const showAreaSuggestions = focusedField && !focusedField.isUpdate && focusedField.id === request._id && focusedField.type === 'area';
                                                    return (
                                                        <>
                                                            <div className="location-input-group">
                                                                <input
                                                                    className="location-input"
                                                                    type="text"
                                                                    placeholder="تعديل المدينة"
                                                                    value={currentCity}
                                                                    onFocus={() => setFocusedField({ id: request._id, type: 'city', isUpdate: false })}
                                                                    onBlur={() => setFocusedField((prev) => (prev && prev.id === request._id && !prev.isUpdate && prev.type === 'city' ? null : prev))}
                                                                    onChange={(e) => setLocationEdit(request._id, { city: e.target.value })}
                                                                />
                                                                {showCitySuggestions && citySuggestions.length > 0 && (
                                                                    <div className="suggestion-list">
                                                                        {citySuggestions.map((name) => (
                                                                            <button
                                                                                key={name}
                                                                                type="button"
                                                                                className="suggestion-item"
                                                                                onMouseDown={(e) => e.preventDefault()}
                                                                                onClick={() => {
                                                                                    setLocationEdit(request._id, { city: name });
                                                                                    setFocusedField(null);
                                                                                }}
                                                                            >
                                                                                {name}
                                                                            </button>
                                                                        ))}
                                                                    </div>
                                                                )}
                                                            </div>
                                                            <div className="location-input-group">
                                                                <input
                                                                    className="location-input"
                                                                    type="text"
                                                                    placeholder="تعديل المنطقة"
                                                                    value={currentArea}
                                                                    onFocus={() => setFocusedField({ id: request._id, type: 'area', isUpdate: false })}
                                                                    onBlur={() => setFocusedField((prev) => (prev && prev.id === request._id && !prev.isUpdate && prev.type === 'area' ? null : prev))}
                                                                    onChange={(e) => setLocationEdit(request._id, { area: e.target.value })}
                                                                />
                                                                {showAreaSuggestions && areaSuggestions.length > 0 && (
                                                                    <div className="suggestion-list">
                                                                        {areaSuggestions.map((name) => (
                                                                            <button
                                                                                key={name}
                                                                                type="button"
                                                                                className="suggestion-item"
                                                                                onMouseDown={(e) => e.preventDefault()}
                                                                                onClick={() => {
                                                                                    setLocationEdit(request._id, { area: name });
                                                                                    setFocusedField(null);
                                                                                }}
                                                                            >
                                                                                {name}
                                                                            </button>
                                                                        ))}
                                                                    </div>
                                                                )}
                                                            </div>
                                                        </>
                                                    );
                                                })()}
                                            </div>
                                            <div className="request-actions">
                                                <button 
                                                    className={`approve ${isActionLoading('approve', request._id) ? 'loading' : ''}`}
                                                    onClick={() => handleApproveClick(request._id)}
                                                    disabled={isActionLoading('approve', request._id)}
                                                >
                                                    {isActionLoading('approve', request._id) ? <FiLoader className="spinner" /> : 'قبول'}
                                                </button>
                                                <button 
                                                    className={`reject ${isActionLoading('reject', request._id) ? 'loading' : ''}`}
                                                    onClick={() => handleRejectClick(request._id)}
                                                    disabled={isActionLoading('reject', request._id)}
                                                >
                                                    {isActionLoading('reject', request._id) ? <FiLoader className="spinner" /> : 'رفض'}
                                                </button>
                                            </div>
                                        </div>
                                    </div>
                                ))
                            )}
                        </div>
                        <button 
                            className="close-btn"
                            onClick={() => setShowAllRequests(false)}
                        >
                            إغلاق
                        </button>
                    </div>
                </div>
            )}

            {/* Update Requests Modal */}
            {showUpdateRequests && (
                <div className="modal">
                    <div className="modal-content requests-modal">
                        <h2>طلبات التحديث</h2>
                        <div className="requests-list">
                            {pendingUpdateRequests.length === 0 ? (
                                <p className="no-requests">لا توجد طلبات</p>
                            ) : (
                                pendingUpdateRequests.map((request) => (
                                    <div key={request._id} className="request-card">
                                        <div className="request-compare">
                                            {[{
                                                key: 'name',
                                                label: 'الاسم',
                                                old: request.driverNameOld || request.driverName || '—',
                                                newer: request.driverNameNew || request.driverName || '—',
                                            }, {
                                                key: 'phone',
                                                label: 'رقم الهاتف',
                                                old: request.phoneNumber || '—',
                                                newer: request.phoneNumber || '—',
                                            }, {
                                                key: 'plate',
                                                label: 'رقم اللوحة',
                                                old: request.carPlateOld || '—',
                                                newer: request.carPlateNew || '—',
                                            }, {
                                                key: 'type',
                                                label: 'نوع المركبة',
                                                old: request.vechicleTypeOld || '—',
                                                newer: request.vechicleTypeNew || '—',
                                            }, {
                                                key: 'model',
                                                label: 'موديل المركبة',
                                                old: request.vechicleModelOld || '—',
                                                newer: request.vechicleModelNew || '—',
                                            }, {
                                                key: 'city',
                                                label: 'المدينة',
                                                old: request.city || '—',
                                                newer: (
                                                    <span className={`flag ${request.cityExists ? 'flag-ok' : 'flag-missing'}`}>
                                                        {request.city || '—'}
                                                    </span>
                                                ),
                                            }, {
                                                key: 'area',
                                                label: 'المنطقة',
                                                old: request.area || '—',
                                                newer: (
                                                    <span className={`flag ${request.areaExists ? 'flag-ok' : 'flag-missing'}`}>
                                                        {request.area || '—'}
                                                    </span>
                                                ),
                                            }].map((field) => (
                                                <div className="compare-row" key={field.key}>
                                                    <div className="compare-cell old">
                                                        <p className="label">{field.label} (قديم)</p>
                                                        <p className="value">{field.old}</p>
                                                    </div>
                                                    <div className="compare-cell new">
                                                        <p className="label">{field.label} (جديد)</p>
                                                        <p className="value">{field.newer}</p>
                                                    </div>
                                                </div>
                                            ))}
                                        </div>

                                        <div className="request-compare media">
                                            {[{
                                                key: 'carImage',
                                                label: 'صورة المركبة',
                                                old: request.carImageOld,
                                                newer: request.carImageNew,
                                            }, {
                                                key: 'licenseFront',
                                                label: 'رخصة القيادة (أمام)',
                                                old: request.driverLicenseFrontOld,
                                                newer: request.driverLicenseFrontNew,
                                            }, {
                                                key: 'licenseBack',
                                                label: 'رخصة القيادة (خلف)',
                                                old: request.driverLicenseBackOld,
                                                newer: request.driverLicenseBackNew,
                                            }].map((media) => (
                                                <div className="compare-row" key={media.key}>
                                                    <div className="compare-cell old">
                                                        <p className="label">{media.label} (قديم)</p>
                                                        {media.old ? (
                                                            <img src={media.old} alt={`${media.label} old`} className="media-thumb" />
                                                        ) : (
                                                            <span className="media-missing">لا يوجد</span>
                                                        )}
                                                    </div>
                                                    <div className="compare-cell new">
                                                        <p className="label">{media.label} (جديد)</p>
                                                        {media.newer ? (
                                                            <img src={media.newer} alt={`${media.label} new`} className="media-thumb" />
                                                        ) : (
                                                            <span className="media-missing">لا يوجد</span>
                                                        )}
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                        <div className="location-edit">
                                            {(() => {
                                                const currentCity = (getLocationEdit(request._id, true).city ?? request.city ?? '').toString();
                                                const currentArea = (getLocationEdit(request._id, true).area ?? request.area ?? '').toString();
                                                const citySuggestions = getCitySuggestions(currentCity);
                                                const areaSuggestions = getAreaSuggestions(currentCity, currentArea);
                                                const showCitySuggestions = focusedField && focusedField.isUpdate && focusedField.id === request._id && focusedField.type === 'city';
                                                const showAreaSuggestions = focusedField && focusedField.isUpdate && focusedField.id === request._id && focusedField.type === 'area';
                                                return (
                                                    <>
                                                        <div className="location-input-group">
                                                            <input
                                                                className="location-input"
                                                                type="text"
                                                                placeholder="تعديل المدينة"
                                                                value={currentCity}
                                                                onFocus={() => setFocusedField({ id: request._id, type: 'city', isUpdate: true })}
                                                                onBlur={() => setFocusedField((prev) => (prev && prev.id === request._id && prev.isUpdate && prev.type === 'city' ? null : prev))}
                                                                onChange={(e) => setLocationEdit(request._id, { city: e.target.value }, true)}
                                                            />
                                                            {showCitySuggestions && citySuggestions.length > 0 && (
                                                                <div className="suggestion-list">
                                                                    {citySuggestions.map((name) => (
                                                                        <button
                                                                            key={name}
                                                                            type="button"
                                                                            className="suggestion-item"
                                                                            onMouseDown={(e) => e.preventDefault()}
                                                                            onClick={() => {
                                                                                setLocationEdit(request._id, { city: name }, true);
                                                                                setFocusedField(null);
                                                                            }}
                                                                        >
                                                                            {name}
                                                                        </button>
                                                                    ))}
                                                                </div>
                                                            )}
                                                        </div>
                                                        <div className="location-input-group">
                                                            <input
                                                                className="location-input"
                                                                type="text"
                                                                placeholder="تعديل المنطقة"
                                                                value={currentArea}
                                                                onFocus={() => setFocusedField({ id: request._id, type: 'area', isUpdate: true })}
                                                                onBlur={() => setFocusedField((prev) => (prev && prev.id === request._id && prev.isUpdate && prev.type === 'area' ? null : prev))}
                                                                onChange={(e) => setLocationEdit(request._id, { area: e.target.value }, true)}
                                                            />
                                                            {showAreaSuggestions && areaSuggestions.length > 0 && (
                                                                <div className="suggestion-list">
                                                                    {areaSuggestions.map((name) => (
                                                                        <button
                                                                            key={name}
                                                                            type="button"
                                                                            className="suggestion-item"
                                                                            onMouseDown={(e) => e.preventDefault()}
                                                                            onClick={() => {
                                                                                setLocationEdit(request._id, { area: name }, true);
                                                                                setFocusedField(null);
                                                                            }}
                                                                        >
                                                                            {name}
                                                                        </button>
                                                                    ))}
                                                                </div>
                                                            )}
                                                        </div>
                                                    </>
                                                );
                                            })()}
                                        </div>
                                        <div className="request-actions">
                                            <button
                                                className={`approve ${isActionLoading('approve-update', request._id) ? 'loading' : ''}`}
                                                onClick={() => handleApproveUpdateClick(request._id)}
                                                disabled={isActionLoading('approve-update', request._id)}
                                            >
                                                {isActionLoading('approve-update', request._id) ? <FiLoader className="spinner" /> : 'قبول'}
                                            </button>
                                            <button
                                                className={`reject ${isActionLoading('reject-update', request._id) ? 'loading' : ''}`}
                                                onClick={() => handleRejectClick(request._id)}
                                                disabled={isActionLoading('reject-update', request._id)}
                                            >
                                                {isActionLoading('reject-update', request._id) ? <FiLoader className="spinner" /> : 'رفض'}
                                            </button>
                                        </div>
                                    </div>
                                ))
                            )}
                        </div>
                        <button
                            className="close-btn"
                            onClick={() => setShowUpdateRequests(false)}
                        >
                            إغلاق
                        </button>
                    </div>
                </div>
            )}

            {/* Driver Info Modal */}
            {showDriverInfo && (
                <div className="modal">
                    <div className="modal-content driver-info-modal">
                        <h2>معلومات السائق</h2>
                        <div className="driver-info">
                            <div className="info-group">
                                <h3>المعلومات الأساسية</h3>
                                <p>رقم السائق: {showDriverInfo.driverNumber || '—'}</p>
                                <p>الاسم: {showDriverInfo.driverName}</p>
                                <p>رقم الهاتف: {showDriverInfo.phoneNumber}</p>
                                <p>تاريخ الانضمام: {showDriverInfo.createdAt ? new Date(showDriverInfo.createdAt).toLocaleDateString('ar-LY') : '—'}</p>
                                <p>المدينة: {showDriverInfo.city || '—'}</p>
                                <p>المنطقة: {showDriverInfo.area || '—'}</p>
                                {showDriverInfo.state !== 'active' && (
                                    <p>سبب عدم التفعيل: {getDriverInactiveReason(showDriverInfo)}</p>
                                )}
                                {showDriverInfo.state === 'deleted' && (
                                    <>
                                        <p>حالة الحساب: محذوف</p>
                                        {showDriverInfo.deletedAt && (
                                            <p>تاريخ الحذف: {new Date(showDriverInfo.deletedAt).toLocaleDateString('ar-LY')}</p>
                                        )}
                                        {showDriverInfo.deletionReason ? (
                                            <p>سبب الحذف: {showDriverInfo.deletionReason}</p>
                                        ) : null}
                                    </>
                                )}
                                <div className="trips-info">
                                    <p>عدد الرحلات: {showDriverInfo.numberOfTrips || 0}</p>
                                    <button
                                        className="view-trips-btn"
                                        onClick={() => {
                                            setShowDriverInfo(null);
                                            navigate(`/trips?driverPhone=${showDriverInfo.phoneNumber || ''}`);
                                        }}
                                    >
                                        عرض الرحلات
                                    </button>
                                </div>
                                <div className="media-row">
                                    {showDriverInfo.carImage && (
                                        <div className="media-item">
                                            <p>صورة المركبة</p>
                                            <img src={showDriverInfo.carImage} alt="car" className="car-image" />
                                        </div>
                                    )}
                                    {showDriverInfo.driverLicenseFront && (
                                        <div className="media-item">
                                            <p>رخصة السائق (أمامي)</p>
                                            <img src={showDriverInfo.driverLicenseFront} alt="license front" className="car-image" />
                                        </div>
                                    )}
                                    {showDriverInfo.driverLicenseBack && (
                                        <div className="media-item">
                                            <p>رخصة السائق (خلفي)</p>
                                            <img src={showDriverInfo.driverLicenseBack} alt="license back" className="car-image" />
                                        </div>
                                    )}
                                </div>
                            </div>

                            <div className="info-group">
                                <h3>التقييمات</h3>
                                {driverRatings.length === 0 ? (
                                    <p>لا توجد تقييمات</p>
                                ) : (
                                    <div className="ratings-list">
                                        <div className="rating-item">
                                            <p>متوسط التقييم</p>
                                            <p style={{ fontSize: '20px', fontWeight: '700' }}>{averageRating} / 5</p>
                                            <p>عدد المراجعات: {driverRatings.length}</p>
                                        </div>
                                    </div>
                                )}
                            </div>

                            <div className="info-group">
                                <h3>الحالة والتحذيرات</h3>
                                <div className="status-controls">
                                    <div className="ban-control">
                                        <p>تعديل رقم السائق</p>
                                        <div className="warnings-buttons">
                                            <input
                                                type="number"
                                                min="1"
                                                value={driverNumberDraft}
                                                onChange={(e) => setDriverNumberDraft(e.target.value)}
                                                style={{ width: 120, padding: '8px 10px' }}
                                            />
                                            <button
                                                onClick={() => handleSaveDriverNumber(showDriverInfo._id)}
                                                disabled={isActionLoading('driver-number', showDriverInfo._id)}
                                                className={isActionLoading('driver-number', showDriverInfo._id) ? 'loading' : ''}
                                            >
                                                {isActionLoading('driver-number', showDriverInfo._id) ? <FiLoader className="spinner" /> : 'حفظ'}
                                            </button>
                                        </div>
                                    </div>
                                </div>
                                <div className="status-controls">
                                    <div className="ban-control">
                                        <p>الحظر: {showDriverInfo.isBanned ? 'محظور' : 'غير محظور'}</p>
                                        <button 
                                            className={`${showDriverInfo.isBanned ? 'unban-btn' : 'ban-btn'} ${isActionLoading('ban', showDriverInfo._id) ? 'loading' : ''}`}
                                            onClick={() => handleToggleBan(showDriverInfo._id)}
                                            disabled={isActionLoading('ban', showDriverInfo._id)}
                                        >
                                            {isActionLoading('ban', showDriverInfo._id) ? 
                                                <FiLoader className="spinner" /> : 
                                                showDriverInfo.isBanned ? 'إلغاء الحظر' : 'حظر'
                                            }
                                        </button>
                                    </div>
                                        <div className="delete-control">
                                            <p>حذف السائق</p>
                                            <button
                                                className={`delete-btn ${isActionLoading('delete', showDriverInfo._id) ? 'loading' : ''}`}
                                                onClick={() => handleDeleteDriver(showDriverInfo._id)}
                                                disabled={isActionLoading('delete', showDriverInfo._id)}
                                                title="حذف السائق"
                                            >
                                                {isActionLoading('delete', showDriverInfo._id) ? <FiLoader className="spinner" /> : <FiTrash2 />}
                                            </button>
                                        </div>
                                    <div className="warnings-control">
                                        <p>عدد التحذيرات: {typeof showDriverInfo.warrnings === 'number' ? showDriverInfo.warrnings : 0}</p>
                                        <div className="warnings-buttons">
                                            <button 
                                                onClick={() => handleUpdateWarnings(
                                                    showDriverInfo._id, 
                                                    showDriverInfo.warrnings + 1
                                                )}
                                                disabled={isActionLoading('warnings', showDriverInfo._id)}
                                                className={isActionLoading('warnings', showDriverInfo._id) ? 'loading' : ''}
                                            >
                                                {isActionLoading('warnings', showDriverInfo._id) ? <FiLoader className="spinner" /> : '+'}
                                            </button>
                                            <button 
                                                onClick={() => handleUpdateWarnings(
                                                    showDriverInfo._id, 
                                                    Math.max(0, showDriverInfo.warrnings - 1)
                                                )}
                                                disabled={showDriverInfo.warrnings === 0 || isActionLoading('warnings', showDriverInfo._id)}
                                                className={isActionLoading('warnings', showDriverInfo._id) ? 'loading' : ''}
                                            >
                                                {isActionLoading('warnings', showDriverInfo._id) ? <FiLoader className="spinner" /> : '-'}
                                            </button>
                                        </div>
                                    </div>
                                </div>
                                {showDriverInfo.state === 'deleted' && (
                                    <div className="status-controls">
                                        <div className="ban-control">
                                            <p>إعادة التفعيل</p>
                                            <button
                                                className={`unban-btn ${isActionLoading('reactivate', showDriverInfo._id) ? 'loading' : ''}`}
                                                onClick={() => handleReactivateDriver(showDriverInfo._id)}
                                                disabled={isActionLoading('reactivate', showDriverInfo._id)}
                                            >
                                                {isActionLoading('reactivate', showDriverInfo._id) ? <FiLoader className="spinner" /> : 'إعادة تفعيل الحساب'}
                                            </button>
                                        </div>
                                    </div>
                                )}
                            </div>

                            <div className="info-group">
                                <h3>طرق الدفع</h3>
                                <div className="status-controls">
                                    <div className="ban-control">
                                        <p>بطاقة مصرفية: {showDriverInfo.doesAcceptCard ? 'مقبولة' : 'غير مقبولة'}</p>
                                        <button
                                            className={`${showDriverInfo.doesAcceptCard ? 'unban-btn' : 'ban-btn'} ${isActionLoading('payment-doesAcceptCard', showDriverInfo._id) ? 'loading' : ''}`}
                                            onClick={() => handleUpdatePaymentMethod(showDriverInfo._id, 'doesAcceptCard', !showDriverInfo.doesAcceptCard)}
                                            disabled={isActionLoading('payment-doesAcceptCard', showDriverInfo._id)}
                                        >
                                            {isActionLoading('payment-doesAcceptCard', showDriverInfo._id) ? <FiLoader className="spinner" /> : showDriverInfo.doesAcceptCard ? 'تعطيل' : 'تفعيل'}
                                        </button>
                                    </div>
                                    <div className="ban-control">
                                        <p>حوالة: {showDriverInfo.doesAcceptTransfer ? 'مقبولة' : 'غير مقبولة'}</p>
                                        <button
                                            className={`${showDriverInfo.doesAcceptTransfer ? 'unban-btn' : 'ban-btn'} ${isActionLoading('payment-doesAcceptTransfer', showDriverInfo._id) ? 'loading' : ''}`}
                                            onClick={() => handleUpdatePaymentMethod(showDriverInfo._id, 'doesAcceptTransfer', !showDriverInfo.doesAcceptTransfer)}
                                            disabled={isActionLoading('payment-doesAcceptTransfer', showDriverInfo._id)}
                                        >
                                            {isActionLoading('payment-doesAcceptTransfer', showDriverInfo._id) ? <FiLoader className="spinner" /> : showDriverInfo.doesAcceptTransfer ? 'تعطيل' : 'تفعيل'}
                                        </button>
                                    </div>
                                </div>
                            </div>
                        </div>
                        <button
                            className="close-btn"
                            onClick={() => setShowDriverInfo(null)}
                        >
                            إغلاق
                        </button>
                    </div>
                </div>
            )}

            {/* Reject Reason Modal */}
            {rejectModal.show && (
                <div className="modal">
                    <div className="modal-content">
                        <h2>سبب الرفض</h2>
                        <div className="input-group">
                            <textarea
                                value={rejectReason}
                                onChange={(e) => setRejectReason(e.target.value)}
                                placeholder="اكتب سبب الرفض هنا..."
                                rows={4}
                            />
                        </div>
                        <div className="modal-actions">
                            <button 
                                className={`P-BTN delete ${isActionLoading('reject', rejectModal.id) || isActionLoading('reject-update', rejectModal.id) ? 'loading' : ''}`}
                                onClick={() => {
                                    if (showUpdateRequests) {
                                        confirmRejectUpdate();
                                    } else {
                                        confirmReject();
                                    }
                                }}
                                disabled={isActionLoading('reject', rejectModal.id) || isActionLoading('reject-update', rejectModal.id)}
                            >
                                {(isActionLoading('reject', rejectModal.id) || isActionLoading('reject-update', rejectModal.id)) ? <FiLoader className="spinner" /> : 'تأكيد الرفض'}
                            </button>
                            <button 
                                className="S-BTN" 
                                onClick={() => {
                                    setRejectModal({ show: false, id: null });
                                    setRejectReason('');
                                }}
                            >
                                إلغاء
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </section>
    );
};